<script lang="ts" setup>
import type { TeacherArticle } from '~/models/Content/TeacherArticle'
import type { Home5to7 } from '~/models/Content/Home5to7'
import type { ContentSectionStandalone } from '~/models/Content/ContentSectionStandalone'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import seedrandom from 'seedrandom'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { firstOf } from '~/utils/queryUtils'
import { gradeTypeNames } from '~/utils/gradeSorter'
import useArrayUtils from '~/utils/arrayUtils'
import useProductStore from '~/stores/product'
import useAuthStore from '~/stores/auth'
import { PendoTrackName } from '~/models/Pendo'
import { GradeType } from '~/models/GradeType'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { ColorName } from '~/models/Content/Color'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useTime from '~/composables/useTime'
import useSubjectButton from '~/composables/useSubjectButton'
import useProductHelper from '~/composables/useProductHelper'
import usePendo from '~/composables/usePendo'
import { useMedia } from '~/composables/useMedia'
import useHome from '~/composables/useHome'
import { useAppColor } from '~/composables/useAppColor'
import useContentApi from '~/api/contentApi'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'
import Image from '~/components/media/Image.vue'
import HomeSubjects from '~/components/home/HomeSubjects.vue'
import Greeting from '~/components/home/Greeting.vue'
import DefaultMessages from '~/components/home/DefaultMessages.vue'
import BannerSharedContent from '~/components/home/BannerSharedContent.vue'
import BannerArticles from '~/components/home/BannerArticles.vue'

useAppColor(ColorName.Green40)

const { t } = useI18n()
const { isStudent } = storeToRefs(useAuthStore())
const { buildResourceUrl } = useUrlBuilder()
const { findContents } = useContentApi()
const { truthy } = useArrayUtils()
const { getStyles } = useSubjectButton()
const { getProductFromResource } = useProductStore()
const { isAddonProduct } = useProductHelper()
const { currentDate } = useTime()
const { pendoTrack, pendoFlushNow } = usePendo()

const { data } = useHome<Home5to7>(ContentType.Home5to7)

const randomSeed = seedrandom(currentDate)

const helpRelationId = computed(() => Number(data?.value?.teacherRelation?.destinationContentId ?? 0))
const bubbleRelationIds = computed(() => data?.value?.bubbleRelation?.destinationContentIds ?? [])
const bubbleRandomId = computed(() => bubbleRelationIds.value[Math.floor(randomSeed() * bubbleRelationIds.value.length)] ?? 0)
const sectionRelationIds = computed(() => data?.value?.sections?.destinationContentIds ?? [])

const relationIds = computed(() => [
  helpRelationId.value,
  bubbleRandomId.value,
  ...sectionRelationIds.value,
].filter(truthy<number>))

const imageRelationIds = computed(() => data?.value?.imageRelations?.destinationContentIds ?? [])
const imageRandomIds = computed(() => [imageRelationIds.value[Math.floor(randomSeed() * imageRelationIds.value.length)] ?? 0])
const { data: image } = firstOf(useMedia(imageRandomIds))

const { data: relations, isLoading } = useQuery({
  enabled: computed(() => relationIds.value.length > 0),
  queryKey: ['home-5-7-relations', 'dont-invalidate-on-grade-change'],
  queryFn: () => findContents<BaseItem>({
    subtreeCriterion: [Subtree.Content, Subtree.App],
    contentIdCriterion: relationIds.value,
    mainLocationCriterion: true,
  }, relationIds.value.length),
  select: data => [...data]
    .sort((a, b) => relationIds.value.indexOf(a.contentId) - relationIds.value.indexOf(b.contentId)),
  staleTime: Infinity,
})

const help = computed(() => (relations.value
  ?.find(({ contentId }) => helpRelationId.value === contentId) as TeacherArticle))
const bubble = computed(() => (relations.value
  ?.find(({ contentId }) => bubbleRelationIds.value.includes(contentId)) as BaseItem))
const sections = computed(() => (relations.value
  ?.filter(({ contentId }) => sectionRelationIds.value.includes(contentId)) as Array<ContentSectionStandalone>))

const product = computed(() => bubble.value && getProductFromResource(bubble.value))
const bubbleRoute = computed(() => bubble?.value && buildResourceUrl(bubble.value))

const subjectOrProductName = computed(() => {
  if (!product.value)
    return ''
  if (isAddonProduct(product.value))
    return product.value.aunivers?.name
  return product.value.subjects?.length ? product.value.subjects[0] : ''
})

const trackBubble = () => {
  pendoTrack(PendoTrackName.HomeFunFact, {
    title: bubble.value?.title ?? '',
    gradeType: gradeTypeNames[GradeType.Grade5To7],
    url: bubbleRoute.value,
    subject: product.value?.subjects[0] ?? '',
  })
  pendoFlushNow()
}
</script>

<template>
  <div class="flex h-full flex-col text-white">
    <h1
      class="sr-only"
      v-text="t('home.title')"
    />
    <DefaultMessages class="relative z-10 mt-24 -mb-18 w-full" />
    <div class="flex size-full flex-wrap items-center gap-6 pt-24 transition-all">
      <BannerSharedContent v-if="isStudent" class="max-w-screen-au mx-auto" />
      <div class="border-green-20 relative w-full border-b pb-16">
        <div class="max-w-screen-au mx-auto grid w-full items-center gap-y-3 px-4 sm:gap-x-3 sm:px-8 md:grid-cols-2">
          <div>
            <Greeting :article="help" class="mb-4 min-h-[12rem] w-fit lg:float-left lg:mr-4 lg:mb-0" />
            <HomeSubjects class="contents" />
          </div>
          <Image
            v-if="image"
            :content="image"
            class="pointer-events-none absolute right-0 bottom-0 !w-[45vw] object-cover"
          />
          <div
            v-if="bubble"
            :style="getStyles(subjectOrProductName)"
            class="text-green-60 pointer-events-auto relative ml-4 flex size-68 flex-col items-center justify-center gap-2 rounded-full bg-(--color) p-4 text-center shadow-lg max-md:mt-12 md:mb-30"
          >
            <span
              class="text-sm font-semibold tracking-wider uppercase"
              v-text="bubble.parentLocationName"
            />
            <span
              class="text-xl font-bold"
              v-text="bubble.title "
            />
            <LinkOrAnchor
              :link="bubbleRoute"
              class="font-medium underline transition hover:text-green-50 focus-visible:ring"
              @click="trackBubble"
            >
              {{ t('readMoreAt') }}
            </LinkOrAnchor>
          </div>
        </div>
      </div>
      <BannerArticles
        :grade-type="GradeType.Grade8To10"
        :is-loading
        :sections="sections ?? []"
        class="max-w-screen-au mx-auto w-full px-4 sm:px-8"
      />
    </div>
  </div>
</template>
